import { SystemProgram } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { bsc, mainnet, arbitrum, optimism } from "wagmi/chains";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  // Prevent the default behavior when space key is pressed
  if (e.key === " ") {
    e.preventDefault();
  }
};

export const handleCopy = (value: string) => {
  navigator.clipboard
    .writeText(value)
    .then(() => {
      alert("Text copied to clipboard!");
    })
    .catch((error) => {
      console.error("Failed to copy:", error);
    });
};

export const toFixed = (x: number): string => {
  let y: string = x.toString();
  if (Math.abs(x) < 1.0) {
    var e = parseInt(x.toString().split("e-")[1]);
    if (e) {
      x *= Math.pow(10, e - 1);
      y = "0." + new Array(e).join("0") + x.toString().substring(2);
    }
  } else {
    var e = parseInt(x.toString().split("+")[1]);
    if (e > 20) {
      e -= 20;
      x /= Math.pow(10, e);
      y += new Array(e + 1).join("0");
    }
  }
  return y;
};

export const getChainIcon = (chainID: number) => {
  switch (chainID) {
    case arbitrum.id:
      return "/icons/chains/arbitrum.svg";
    case bsc.id:
      return "/icons/chains/bsc.png";
    case mainnet.id:
      return "/icons/chains/ethereum.png";
    case optimism.id:
      return "/icons/chains/base.svg";
    default:
      return "/icons/chains/optimism.png";
  }
};

// Function to truncate Ethereum addresses
export const truncateAddress = (address: string) => {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

// Function to format USD amounts
export const formatUSD = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const isValidNumber = (value: string) => {
  const regex = /^\d+\.?\d*$/;
  if (value === "") {
    return true;
  }
  return regex.test(value);
};

export function isValidSolanaAddress(address: string): boolean {
  // Early return for empty or invalid type
  if (!address || typeof address !== "string") {
    return false;
  }

  // Check basic string format with exact length (Solana addresses are 44 characters)
  const base58Regex =
    /^[1-9A-HJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{32,44}$/;
  if (!base58Regex.test(address)) {
    return false;
  }

  try {
    // Create public key and verify it's valid
    const publicKey = new PublicKey(address);

    // Ensure it's not the system program address
    if (publicKey.equals(SystemProgram.programId)) {
      return false;
    }

    // Check if it's on the ed25519 curve
    if (!PublicKey.isOnCurve(publicKey.toBytes())) {
      return false;
    }

    // Verify address consistency
    const reconstructed = publicKey.toBase58();
    if (reconstructed !== address) {
      return false;
    }

    return true;
  } catch (_error) {
    return false;
  }
}

export const stripSeparators = (formattedString: string) => {
  // Remove locale-specific separators and symbols to get a clean number string
  const cleanedString = formattedString.replace(/[$, ]/g, '');
  return cleanedString;
};

export const addSeparators = (value: string | number) => {
  const locale = 'en-US';
  const options = { style: 'decimal' as const, minimumFractionDigits: 0, maximumFractionDigits: 9 };
  const formatter = new Intl.NumberFormat(locale, options);

  if (!value || value === '0' || value === 0) return value.toString();
  const num: number = typeof value === 'string' ? parseFloat(value) : value;
  if (num <= 0.1) return value.toLocaleString();
  return formatter.format(num)
};

export const displayInputNumber = (value: string | number, decimals: number = 2): string => {
  if (!value || value === '0' || value === 0) return value.toLocaleString();
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num < 0.01) return value.toLocaleString();
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  });
};